/**
 * Per-variant media filtering for the main product gallery.
 *
 * Grouping is driven entirely by alt text, with the selected variant's own
 * assigned image choosing the group:
 *
 *   1. The shopper selects a variant.
 *   2. We read the alt text of that variant's assigned (featured) image.
 *   3. Every image sharing that alt text stays; the rest are detached from the
 *      DOM (see apply() for why detaching rather than hiding).
 *
 * Alt text is therefore an arbitrary group label - it never has to match the
 * option name, and needs no special markers. Shopify's native variant-image
 * assignment does the linking.
 *
 * Rules:
 *   - Blank alt text on an image  -> shared, shown for every variant.
 *   - Alt text equal to the product title -> also shared. Shopify auto-fills the
 *     title when alt text is left empty on upload, so this is what "blank"
 *     actually looks like in practice for a lot of media.
 *   - Variant image has blank alt -> no grouping is possible, show everything.
 *   - Variant has no image at all -> show everything.
 *   - Matching trims whitespace and ignores case.
 *
 * Attached to the ProductMedia custom element in product-media.js; the entry
 * point is filterByVariant(variantId, featuredMediaId).
 */
(() => {
  if (window.VariantMediaFilter) {
    return;
  }

  const SELECTORS = {
    mediaItem: '[data-product-media]',
    thumbItem: '[data-product-thumb]'
  };

  const HIDDEN_CLASS = 'is-variant-hidden';

  // Marks the first still-attached slide, so the "grid with large first item"
  // layout has something to enlarge once filtering removes the original first one.
  const FIRST_VISIBLE_CLASS = 'is-variant-first';

  /**
   * Normalises an alt string for comparison: trims surrounding whitespace,
   * collapses runs of inner whitespace, and lowercases. Guards against a stray
   * trailing space silently splitting a group in two.
   */
  const normalise = (value) => {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  };

  const altOf = (element) => normalise(element.dataset.mediaAlt);

  const VariantMediaFilter = {
    HIDDEN_CLASS,
    FIRST_VISIBLE_CLASS,
    selectors: SELECTORS,
    normalise,

    /**
     * The product title, normalised. Alt text matching it counts as blank,
     * because Shopify fills the title in when alt text is left empty.
     */
    productTitle(root) {
      return normalise(root.dataset.productTitle);
    },

    /**
     * True when a media item should show for every variant: no alt text, or alt
     * text that is really just Shopify's auto-filled product title.
     */
    isShared(alt, productTitle) {
      return alt === '' || (productTitle !== '' && alt === productTitle);
    },

    /**
     * Looks up the alt text of the image assigned to a variant, using the
     * variant-id -> media-alt map emitted alongside the gallery.
     * Returns '' when the variant has no assigned image or that image has no alt.
     */
    variantAlt(root, variantId) {
      if (!variantId) {
        return '';
      }

      const map = root.dataset.variantMediaAlts;

      if (!map) {
        return '';
      }

      let parsed;

      try {
        parsed = JSON.parse(map);
      } catch (error) {
        return '';
      }

      return normalise(parsed[String(variantId)]);
    },

    /**
     * Decides which media ids should be visible for a variant.
     * Returns null when filtering should not apply, so callers can leave the
     * gallery untouched rather than risk showing an empty or misleading set.
     */
    resolve(root, variantId) {
      if (!root) {
        return null;
      }

      const groupKey = this.variantAlt(root, variantId);
      const productTitle = this.productTitle(root);

      // The variant has no assigned image, or that image carries no real alt
      // text to group by. Show everything.
      if (!groupKey || this.isShared(groupKey, productTitle)) {
        return null;
      }

      // Consult every slide, including any currently detached by a previous
      // filter pass - otherwise each successive variant change could only ever
      // narrow the set further.
      const items = this.allMediaItems(root);

      if (items.length === 0) {
        return null;
      }

      const visible = items.filter((item) => {
        const alt = altOf(item);

        return this.isShared(alt, productTitle) || alt === groupKey;
      });

      // Nothing actually matched the group, only shared images did. Filtering
      // here would hide the variant's own image, so leave the gallery alone.
      const matchedGroup = visible.some((item) => altOf(item) === groupKey);

      if (!matchedGroup) {
        return null;
      }

      return visible.map((item) => item.dataset.mediaId);
    },

    /**
     * Shows only the given media ids in both the main slider and the thumbs.
     * Passing null reveals everything again.
     *
     * Filtered-out slides are DETACHED from the DOM rather than hidden with
     * CSS. Swiper's loopCreate numbers slides by class match with no `display`
     * check, so a `display: none` slide still consumes a loop index even though
     * updateSlides gives it no position - leaving the carousel able to swipe
     * onto nothing. Removing the elements keeps Swiper's slide count honest.
     *
     * Every slide is remembered on first call along with its original position,
     * so a later variant change can put it back in the right place.
     */
    apply(root, visibleIds) {
      const showAll = visibleIds === null;

      this.rememberSlides(root);

      const store = root.__variantMediaSlides;

      if (!store) {
        return;
      }

      [SELECTORS.mediaItem, SELECTORS.thumbItem].forEach((selector) => {
        const entries = store[selector];

        if (!entries) {
          return;
        }

        entries.forEach((entry) => {
          const { element, parent } = entry;
          const isVisible = showAll || visibleIds.includes(element.dataset.mediaId);

          if (isVisible) {
            if (!element.isConnected) {
              this.reinsert(entry, entries);
            }

            element.classList.remove(HIDDEN_CLASS);
            element.removeAttribute('aria-hidden');

            return;
          }

          // Marked as well as detached, so any code still holding a reference
          // can tell the difference, and so re-showing is a class removal.
          element.classList.add(HIDDEN_CLASS);
          element.setAttribute('aria-hidden', 'true');

          if (element.isConnected && parent) {
            element.remove();
          }
        });
      });

      this.markFirstVisible(root);
    },

    /**
     * Every main-slider media item, attached or not. Falls back to the live DOM
     * before the first apply() has taken a snapshot.
     */
    allMediaItems(root) {
      const store = root.__variantMediaSlides;

      if (store && store[SELECTORS.mediaItem]) {
        return store[SELECTORS.mediaItem].map((entry) => entry.element);
      }

      return Array.from(root.querySelectorAll(SELECTORS.mediaItem));
    },

    /**
     * Snapshots every slide and its original sibling order, once per element.
     */
    rememberSlides(root) {
      if (root.__variantMediaSlides) {
        return;
      }

      const store = {};

      [SELECTORS.mediaItem, SELECTORS.thumbItem].forEach((selector) => {
        store[selector] = Array.from(root.querySelectorAll(selector)).map((element, index) => ({
          element,
          parent: element.parentElement,
          index
        }));
      });

      root.__variantMediaSlides = store;
    },

    /**
     * Puts a detached slide back at its original position, using the nearest
     * still-attached later sibling as the anchor.
     */
    reinsert(entry, entries) {
      const { element, parent, index } = entry;

      if (!parent) {
        return;
      }

      const nextAttached = entries.find(
        (candidate) => candidate.index > index && candidate.element.isConnected
      );

      if (nextAttached) {
        parent.insertBefore(element, nextAttached.element);

        return;
      }

      parent.appendChild(element);
    },

    /**
     * Flags the first visible slide for the "grid with large first item"
     * layout. Filtered slides are detached, so this is normally just the first
     * child; the explicit marker keeps the CSS working regardless.
     */
    markFirstVisible(root) {
      const items = root.querySelectorAll(SELECTORS.mediaItem);
      let seenVisible = false;

      items.forEach((element) => {
        const isFirstVisible = !seenVisible && !element.classList.contains(HIDDEN_CLASS);

        element.classList.toggle(FIRST_VISIBLE_CLASS, isFirstVisible);

        if (isFirstVisible) {
          seenVisible = true;
        }
      });
    },

    /**
     * The number of slides left after filtering, used to keep Swiper's drag
     * behaviour honest when a variant resolves to a single image.
     */
    visibleCount(root) {
      return root.querySelectorAll(`${SELECTORS.mediaItem}:not(.${HIDDEN_CLASS})`).length;
    }
  };

  window.VariantMediaFilter = VariantMediaFilter;
})();
